import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowRight } from 'lucide-react';
import Card from '../../shared/Card';
import Hyperlink from '../../Hyperlink';
import tileBodyFit from './tileBodyFit';
import './ProductsGridTile.css';

/**
 * ProductsGridTile — a content tile ProductsGrid places between its product
 * cards (`tiles` prop). Part of the block, like its header and CTA — not a
 * standalone component; ProductsGrid sizes it (see ProductsGrid.css).
 *
 * Figma OVER-LL:
 * | Variant | Block      | Tile       |
 * |---------|------------|------------|
 * | text    | 2699:18385 | 2699:18559 |
 * | image   | 2707:18924 | 2707:18937 |
 *
 * - text: the M2 card surface; H2 + P.Large gray-6 paragraphs + a regular
 *   Hyperlink, centred vertically.
 * - image: a cover photo in the small-m frame with a gradient foot, an H2 in
 *   gray-025 and a large white Hyperlink (the same treatment as the BentoGrid
 *   feature card).
 *
 * The link is the only interactive element: a real `<a href>`, so search
 * engines follow it. Without an `href` it still runs `onClick` (SPA routing)
 * and does not navigate.
 *
 * When ProductsGrid binds the text tile to the card rhythm (desktop), the
 * paragraphs only get the height the title and the link leave. The body is
 * measured (ResizeObserver) and marked `data-fit`: "fits", "clipped" (cut
 * under a fade) or "hidden" (fewer than three lines would show) — see
 * tileBodyFit.js. Elsewhere the tile grows with its copy and it always fits.
 */
const ProductsGridTile = ({
  variant = 'text',
  title,
  titleAs: Title = 'h3',
  body,
  image,
  imageAlt = '',
  linkLabel,
  href,
  onClick,
  className = '',
}) => {
  const paragraphs = body == null ? [] : [].concat(body).filter(Boolean);
  const isImage = variant === 'image';
  const hasBody = !isImage && paragraphs.length > 0;
  const bodyRef = useRef(null);
  const [fit, setFit] = useState('fits');
  const bodyKey = paragraphs.join('\n');

  /* Measure before paint (no flash of uncut copy); re-measure whenever the
     box resizes — the tile follows its track width, the copy wraps anew */
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return undefined;
    const measure = () => setFit(tileBodyFit({
      scrollHeight: el.scrollHeight,
      clientHeight: el.clientHeight,
      lineHeight: parseFloat(window.getComputedStyle(el).lineHeight),
    }));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [hasBody, bodyKey]);

  const handleLink = (e) => {
    if (!href) e.preventDefault();
    onClick?.(e);
  };

  const link = linkLabel && (
    <Hyperlink
      variant={isImage ? 'large' : 'regular'}
      href={href || '#'}
      onClick={handleLink}
      iconRight={<ArrowRight size={20} />}
      showIconRight
      className="ds-products-grid-tile__link"
    >
      {linkLabel}
    </Hyperlink>
  );

  const heading = title && <Title className="ds-products-grid-tile__title typo-h2">{title}</Title>;

  return (
    <Card
      as="article"
      className={['ds-products-grid-tile', `ds-products-grid-tile--${isImage ? 'image' : 'text'}`, className].filter(Boolean).join(' ')}
    >
      {isImage ? (
        <div className="ds-products-grid-tile__media">
          {image && <img className="ds-products-grid-tile__img" src={image} alt={imageAlt} loading="lazy" />}
          <div className="ds-products-grid-tile__copy">
            {heading}
            {link}
          </div>
        </div>
      ) : (
        <div className="ds-products-grid-tile__copy">
          {heading}
          {hasBody && (
            <div ref={bodyRef} className="ds-products-grid-tile__body typo-body-lg" data-fit={fit}>
              {paragraphs.map((text, i) => <p key={i}>{text}</p>)}
            </div>
          )}
          {link}
        </div>
      )}
    </Card>
  );
};

ProductsGridTile.propTypes = {
  /** "text" (Figma 2699:18559) or "image" (Figma 2707:18937) */
  variant: PropTypes.oneOf(['text', 'image']),
  /** Heading (rendered with the H2 style) */
  title: PropTypes.string,
  /** Heading element — h3 by default, under the section's own h2 */
  titleAs: PropTypes.elementType,
  /** Text tile copy: one string, or one string per paragraph */
  body: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  /** Image tile photo URL */
  image: PropTypes.string,
  /** Alt text for the photo ("" when it is decorative) */
  imageAlt: PropTypes.string,
  /** Link text (e.g. "Discover more"); no link without it */
  linkLabel: PropTypes.string,
  /** Link URL */
  href: PropTypes.string,
  /** Link click handler (runs with or without an href) */
  onClick: PropTypes.func,
  /** Additional CSS class (ProductsGrid passes its layout classes here) */
  className: PropTypes.string,
};

export default ProductsGridTile;
